/* eslint-disable react/display-name */

'use client';

import React, {
  memo,
  useMemo,
  useRef,
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import throttle from 'lodash-es/throttle';

import {
  classNameBind,
  isEmpty,
  isString,
} from '@wconcept/wconcept-ui/utils/common';
import { sanitizeContent } from '@wconcept/wconcept-ui/utils/htmlSanitizer';

import * as S from './style';

export interface IButtonProps {
  id?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  href?: string;
  children?: React.ReactNode;
  label?: string;
  mode?: string;
  color?: string;
  size?: string;
  className?: string;
  disabled?: boolean;
  target?: string;
  rel?: string;
  ref?: unknown;
  icon?: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => Promise<void> | void;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onFocus?: (e: React.FocusEvent<HTMLButtonElement, Event>) => void;
  onBlur?: (e: React.FocusEvent<HTMLButtonElement, MouseEvent>) => void;
  role?: string;
  ariaControls?: string;
  ariaSelected?: boolean;
  ariaExpanded?: boolean;
}

const Button = forwardRef(
  (
    {
      id,
      type = 'button',
      href,
      mode,
      color,
      size,
      className,
      disabled,
      target,
      rel,
      icon,
      label,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      children,
      role,
      ariaControls,
      ariaSelected,
      ariaExpanded,
    }: IButtonProps,
    ref
  ) => {
    const btnRef = useRef<unknown>(ref || null);
    const [isDisabled, setIsDisabled] = useState(disabled || false);

    useEffect(() => {
      setIsDisabled(disabled || false);
    }, [disabled]);

    // 디바운스된 onClick 핸들러 생성
    const onThrottleClick = useMemo(
      () =>
        throttle(async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.persist(); // React 이벤트 객체를 유지
          try {
            if (onClick) {
              if (onClick instanceof Promise) {
                await onClick(e);
              } else {
                onClick(e);
              }
            }
          } catch {
            console.error('Button onClick Error');
          }
        }, 400),
      [onClick]
    );

    const param: any = {
      id: id || undefined,
      href: href || undefined,
      ref: btnRef,
      className: useMemo(
        () =>
          classNameBind([
            'btn',
            disabled ? 'disabled' : '',
            mode ? `mode-${mode}` : '',
            color || '',
            size || '',
            className || '',
          ]),
        [disabled, mode, color, size, className]
      ),
      type: href ? null : type,
      target,
      rel: target ? rel : null,
      $icon: icon,
      role,
      'aria-label': label,
      'aria-controls': ariaControls,
      'aria-selected': ariaSelected,
      'aria-expanded': ariaExpanded,
      onClick: onThrottleClick,
      onMouseEnter: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onMouseEnter) {
          onMouseEnter(e);
        }
      },
      onMouseLeave: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (onMouseLeave) {
          onMouseLeave(e);
        }
      },
      onFocus: (e: React.FocusEvent<HTMLButtonElement, Event>) => {
        if (onFocus) {
          onFocus(e);
        }
      },
    };

    // ITEM RENDERER : getChildrenRender
    const getChildrenRender = useCallback(() => {
      const str = (el: React.ReactNode) => String(el);
      return (
        <>
          {!isEmpty(icon) && icon}
          {!isEmpty(children) && isString(children) ? (
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizeContent(str(children)),
              }}
            />
          ) : (
            children
          )}
        </>
      );
    }, [children, icon]);

    return (
      <S.Button {...param} as={href ? 'a' : 'button'} disabled={isDisabled}>
        {getChildrenRender()}
      </S.Button>
    );
  }
);

export default memo(Button);
